import { useEffect, useState } from "react";
import { adminOpsStatus, adminOpsRunbook } from "@/lib/api";
import { CheckCircle2, AlertTriangle, AlertCircle, HelpCircle, ChevronDown, ChevronRight, RefreshCw, BookOpen, ExternalLink } from "lucide-react";
import ReactMarkdown from "react-markdown";
import {
    Sheet,
    SheetContent,
    SheetHeader,
    SheetTitle,
    SheetDescription,
} from "@/components/ui/sheet";

// Single-pill ops dashboard pinned to the top of /admin. Aggregates
// every tripwire (preflight, backup health, Stripe webhook, DR drill,
// URL smoke) into one "is anything broken?" answer at a glance.
// Click to expand for the per-tripwire grid.

const PILL_CONFIG = {
    green: {
        label: "ALL GREEN",
        bg: "var(--bl-moss-tint)",
        color: "var(--bl-moss)",
        border: "var(--bl-moss)",
        icon: CheckCircle2,
        sentence: "Every tripwire is healthy.",
    },
    warn: {
        label: "WARNING",
        bg: "#FAF1DC",
        color: "#8E6A1F",
        border: "#C29A45",
        icon: AlertTriangle,
        sentence: "Surfaced a warning — investigate when convenient.",
    },
    red: {
        label: "ALERT",
        bg: "var(--bl-rust-tint)",
        color: "var(--bl-rust)",
        border: "var(--bl-rust)",
        icon: AlertCircle,
        sentence: "Something is failing — fix needed.",
    },
    unknown: {
        label: "UNKNOWN",
        bg: "var(--bl-bg)",
        color: "var(--bl-muted)",
        border: "var(--bl-line)",
        icon: HelpCircle,
        sentence: "Crons haven't run yet — give it a few minutes.",
    },
};

const TILE_STATUS_CONFIG = {
    healthy: { dot: "var(--bl-moss)", label: "Healthy" },
    warn: { dot: "#C29A45", label: "Warning" },
    red: { dot: "var(--bl-rust)", label: "Failing" },
    unknown: { dot: "var(--bl-line)", label: "No data" },
};

// Visual pill shown next to "Operations status" so admins know which
// environment's tripwires they're looking at. Preview + Production
// share the same MongoDB but snapshots are env-scoped — this pill is
// the at-a-glance disambiguator.
const ENV_PILL_CONFIG = {
    prod: {
        label: "PROD",
        bg: "var(--bl-rust-tint)",
        color: "var(--bl-rust)",
        title: "Production environment — what real customers see.",
    },
    preview: {
        label: "PREVIEW",
        bg: "#E0E7FF",
        color: "#3730A3",
        title: "Preview environment — sandbox for testing.",
    },
    staging: {
        label: "STAGING",
        bg: "#FAF1DC",
        color: "#8E6A1F",
        title: "Staging environment.",
    },
};

function envPill(envLabel) {
    if (!envLabel) return null;
    const key = String(envLabel).toLowerCase();
    const cfg = ENV_PILL_CONFIG[key] || {
        label: key.toUpperCase(),
        bg: "var(--bl-bg)",
        color: "var(--bl-muted)",
        title: `Environment: ${envLabel}`,
    };
    return cfg;
}

function relativeAge(iso) {
    if (!iso) return "—";
    try {
        const d = new Date(iso);
        const diff = (Date.now() - d.getTime()) / 1000;
        if (diff < 60) return `${Math.floor(diff)}s ago`;
        if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
        if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
        return `${Math.floor(diff / 86400)}d ago`;
    } catch {
        return "—";
    }
}

export default function OpsStatusTile() {
    const [data, setData] = useState(null);
    const [open, setOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    // Runbook sheet — single shared instance, opened with the
    // selected tripwire's markdown content.
    const [runbookId, setRunbookId] = useState(null);
    const [runbookContent, setRunbookContent] = useState(null);
    const [runbookLoading, setRunbookLoading] = useState(false);

    const openRunbook = async (tripwireId, tripwireLabel) => {
        setRunbookId(tripwireId);
        setRunbookContent({ label: tripwireLabel, markdown: null, error: null });
        setRunbookLoading(true);
        try {
            const r = await adminOpsRunbook(tripwireId);
            if (r.ok) {
                setRunbookContent({
                    label: tripwireLabel,
                    markdown: r.markdown,
                    error: null,
                    last_edited_at: r.last_edited_at,
                    last_edited_by: r.last_edited_by,
                    last_edited_subject: r.last_edited_subject,
                    github_edit_url: r.github_edit_url,
                    rel_path: r.rel_path,
                });
            } else {
                setRunbookContent({ label: tripwireLabel, markdown: null, error: r.error || "Failed to load runbook." });
            }
        } catch (e) {
            setRunbookContent({
                label: tripwireLabel, markdown: null,
                error: e?.response?.data?.detail || "Failed to load runbook.",
            });
        } finally {
            setRunbookLoading(false);
        }
    };

    const relativeTimeFromIso = (iso) => {
        if (!iso) return null;
        try {
            const d = new Date(iso);
            const diff = (Date.now() - d.getTime()) / 1000;
            if (diff < 60) return "just now";
            if (diff < 3600) return `${Math.floor(diff / 60)} min ago`;
            if (diff < 86400) return `${Math.floor(diff / 3600)} h ago`;
            if (diff < 86400 * 30) return `${Math.floor(diff / 86400)} d ago`;
            if (diff < 86400 * 365) return `${Math.floor(diff / (86400 * 30))} mo ago`;
            return `${Math.floor(diff / (86400 * 365))} y ago`;
        } catch {
            return null;
        }
    };

    const load = async () => {
        setLoading(true);
        try {
            const r = await adminOpsStatus();
            setData(r);
        } catch {
            /* non-blocking */
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
        const id = setInterval(load, 60_000);  // refresh every 60s
        return () => clearInterval(id);
    }, []);

    if (!data) {
        return (
            <div
                className="bl-card p-4 mb-4 text-sm text-bl-muted"
                data-testid="admin-ops-status-tile"
            >
                Loading ops status…
            </div>
        );
    }

    const cfg = PILL_CONFIG[data.overall_status] || PILL_CONFIG.unknown;
    const Icon = cfg.icon;
    const envCfg = envPill(data.env_label);

    return (
        <div
            className="bl-card p-4 mb-4 transition-colors"
            style={{
                borderColor: cfg.border,
                background: `linear-gradient(to right, ${cfg.bg}, transparent 60%)`,
            }}
            data-testid="admin-ops-status-tile"
        >
            <button
                type="button"
                onClick={() => setOpen((v) => !v)}
                className="w-full flex items-center justify-between gap-3 text-left"
                data-testid="admin-ops-status-toggle"
            >
                <div className="flex items-center gap-3 min-w-0">
                    <Icon
                        className="w-7 h-7 shrink-0"
                        style={{ color: cfg.color }}
                        aria-hidden
                    />
                    <div className="min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                            <span
                                className="px-2 py-0.5 rounded text-[10px] uppercase font-display font-bold tracking-wider"
                                style={{ background: cfg.bg, color: cfg.color }}
                                data-testid="admin-ops-status-pill"
                            >
                                {cfg.label}
                            </span>
                            {envCfg && (
                                <span
                                    className="px-2 py-0.5 rounded text-[10px] uppercase font-display font-bold tracking-wider"
                                    style={{ background: envCfg.bg, color: envCfg.color }}
                                    title={envCfg.title}
                                    data-testid="admin-ops-env-pill"
                                >
                                    {envCfg.label}
                                </span>
                            )}
                            <span className="font-display font-semibold text-bl text-base">
                                Operations status
                            </span>
                        </div>
                        <div className="text-xs text-bl-muted mt-0.5">
                            {cfg.sentence}{" "}
                            <span className="text-bl">
                                {data.counts?.healthy || 0} healthy ·{" "}
                                {data.counts?.warn || 0} warn ·{" "}
                                {data.counts?.red || 0} alert
                                {(data.counts?.unknown || 0) > 0
                                    ? ` · ${data.counts.unknown} no-data`
                                    : ""}
                            </span>
                        </div>
                    </div>
                </div>
                <div className="flex items-center gap-1 shrink-0 text-bl-muted">
                    <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); load(); }}
                        disabled={loading}
                        aria-label="Refresh"
                        className="p-1 hover:text-bl transition-colors"
                        data-testid="admin-ops-status-refresh"
                    >
                        <RefreshCw className={"w-3.5 h-3.5 " + (loading ? "animate-spin" : "")} />
                    </button>
                    {open
                        ? <ChevronDown className="w-4 h-4" aria-hidden />
                        : <ChevronRight className="w-4 h-4" aria-hidden />}
                </div>
            </button>

            {open && (
                <div
                    className="mt-3 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2"
                    data-testid="admin-ops-status-grid"
                >
                    {(data.tripwires || []).map((t) => {
                        const sc = TILE_STATUS_CONFIG[t.status] || TILE_STATUS_CONFIG.unknown;
                        const drift = t.cross_env;
                        return (
                            <div
                                key={t.id}
                                className="border border-bl rounded-md p-2.5 bg-bl-surface"
                                data-testid={`ops-status-tile-${t.id}`}
                            >
                                <div className="flex items-center gap-2">
                                    <span
                                        className="w-2 h-2 rounded-full shrink-0"
                                        style={{ background: sc.dot }}
                                        aria-label={sc.label}
                                    />
                                    <div className="font-display font-semibold text-bl text-xs flex-1 truncate">
                                        {t.label}
                                    </div>
                                    {t.runbook_stale && (
                                        <span
                                            className="text-[9px] uppercase font-display font-bold tracking-wider px-1 py-0.5 rounded shrink-0"
                                            style={{ background: "#FAF1DC", color: "#8E6A1F" }}
                                            title={`Runbook hasn't been updated since the last failure — last failure ${relativeAge(t.runbook_stale.last_failure_at)}, runbook ${relativeAge(t.runbook_stale.runbook_edited_at)}`}
                                            data-testid={`ops-status-tile-${t.id}-stale-pill`}
                                        >
                                            STALE
                                        </span>
                                    )}
                                    <span className="text-[10px] text-bl-muted tabular-nums shrink-0">
                                        {relativeAge(t.checked_at)}
                                    </span>
                                </div>
                                <div className="mt-1 text-[11px] text-bl-muted leading-snug">
                                    {t.message || "—"}
                                </div>
                                <button
                                    type="button"
                                    onClick={() => openRunbook(t.id, t.label)}
                                    className="mt-1.5 inline-flex items-center gap-1 text-[10px] text-bl-muted hover:text-bl transition-colors"
                                    title="Read the ops runbook for this tripwire"
                                    data-testid={`ops-status-tile-${t.id}-runbook`}
                                >
                                    <BookOpen className="w-3 h-3" aria-hidden />
                                    <span className="underline-offset-2 hover:underline">View runbook →</span>
                                </button>
                                {drift && (
                                    <div
                                        className="mt-1.5 flex items-start gap-1.5 text-[10px] leading-snug rounded px-1.5 py-1"
                                        style={{ background: "#FAF1DC", color: "#8E6A1F" }}
                                        title={`${drift.env_label} reports: ${drift.message || drift.status}`}
                                        data-testid={`ops-status-tile-${t.id}-drift`}
                                    >
                                        <AlertTriangle className="w-3 h-3 shrink-0 mt-0.5" aria-hidden />
                                        <span>
                                            <b style={{ textTransform: "uppercase" }}>{drift.env_label}</b>
                                            {" "}drifted{" "}
                                            <span className="tabular-nums">{relativeAge(drift.checked_at)}</span>
                                            {" — "}
                                            {(TILE_STATUS_CONFIG[drift.status] || TILE_STATUS_CONFIG.unknown).label.toLowerCase()}
                                        </span>
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            )}

            <Sheet open={!!runbookId} onOpenChange={(v) => { if (!v) setRunbookId(null); }}>
                <SheetContent
                    side="right"
                    className="w-full sm:max-w-xl overflow-y-auto"
                    data-testid="ops-runbook-sheet"
                >
                    <SheetHeader>
                        <SheetTitle data-testid="ops-runbook-title">
                            {runbookContent?.label || "Ops runbook"}
                        </SheetTitle>
                        <SheetDescription className="text-xs space-y-1">
                            <span className="block">
                                Self-serve diagnosis aid — edit the source MD file in
                                <code className="mx-1 px-1 bg-bl-bg rounded text-[10px]">backend/ops_runbooks/{runbookId}.md</code>
                                and Save-to-GitHub to publish updates.
                            </span>
                            {runbookContent?.last_edited_at && (
                                <span
                                    className="block text-[11px] text-bl-muted"
                                    data-testid="ops-runbook-attribution"
                                >
                                    Last edited{" "}
                                    <span title={runbookContent.last_edited_at}>
                                        <b>{relativeTimeFromIso(runbookContent.last_edited_at) || runbookContent.last_edited_at}</b>
                                    </span>
                                    {runbookContent.last_edited_by ? (
                                        <> by <b>{runbookContent.last_edited_by}</b></>
                                    ) : null}
                                    {runbookContent.last_edited_subject ? (
                                        <span className="text-bl-muted"> · &ldquo;{runbookContent.last_edited_subject}&rdquo;</span>
                                    ) : null}
                                </span>
                            )}
                            {runbookContent?.github_edit_url && (
                                <a
                                    href={runbookContent.github_edit_url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="inline-flex items-center gap-1 text-[11px] text-bl hover:underline mt-1"
                                    data-testid="ops-runbook-github-link"
                                >
                                    <ExternalLink className="w-3 h-3" aria-hidden />
                                    Open in GitHub
                                </a>
                            )}
                        </SheetDescription>
                    </SheetHeader>
                    <div className="mt-4" data-testid="ops-runbook-body">
                        {runbookLoading && (
                            <div className="text-sm text-bl-muted">Loading runbook…</div>
                        )}
                        {!runbookLoading && runbookContent?.error && (
                            <div className="text-sm text-[var(--bl-rust)]">
                                {runbookContent.error}
                            </div>
                        )}
                        {!runbookLoading && runbookContent?.markdown && (
                            <article className="text-sm text-bl leading-relaxed [&_h1]:text-base [&_h1]:font-display [&_h1]:font-semibold [&_h1]:mt-4 [&_h1]:mb-2 [&_h2]:text-sm [&_h2]:font-display [&_h2]:font-semibold [&_h2]:mt-4 [&_h2]:mb-2 [&_h3]:text-xs [&_h3]:font-display [&_h3]:font-semibold [&_h3]:uppercase [&_h3]:tracking-wider [&_h3]:mt-3 [&_h3]:mb-1.5 [&_h3]:text-bl-muted [&_p]:mb-2 [&_ul]:list-disc [&_ul]:pl-5 [&_ul]:mb-3 [&_ol]:list-decimal [&_ol]:pl-5 [&_ol]:mb-3 [&_li]:mb-1 [&_code]:bg-bl-bg [&_code]:px-1 [&_code]:rounded [&_code]:text-[12px] [&_code]:text-bl [&_strong]:text-bl [&_strong]:font-semibold">
                                <ReactMarkdown>{runbookContent.markdown}</ReactMarkdown>
                            </article>
                        )}
                    </div>
                </SheetContent>
            </Sheet>
        </div>
    );
}
